/**
 * Layout shell for the Relationship Manager (role=manager) portal.
 * Mirrors the IB shell so the UX feels consistent across portals.
 */
import { useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/useAuth";
import { Brand } from "./Brand";
import { Topbar } from "./Topbar";
import { managerNav } from "./managerNav";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/utils";

export function ManagerShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  if (isPending) {
    return (
      <div className="min-h-screen bg-bg flex">
        <div className="w-60 border-r border-border p-4 space-y-2">
          <Skeleton className="h-9 w-32" />
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
        <div className="flex-1 p-8 space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (me.role !== "manager") {
    if (me.role === "platform_admin") return <Navigate to="/platform/dashboard" replace />;
    if (me.role === "admin") return <Navigate to="/admin/dashboard" replace />;
    if (me.role === "ib") return <Navigate to="/ib/dashboard" replace />;
    return <Navigate to="/trader/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-bg flex">
      {navOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setNavOpen(false)} aria-hidden />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 h-screen w-60 shrink-0 border-r border-border bg-bg-subtle flex flex-col transition-transform duration-200 lg:sticky lg:top-0 lg:translate-x-0 lg:z-auto lg:bg-bg-subtle/50 ${navOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex h-16 items-center px-5 border-b border-border">
          <Brand />
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" onClick={() => setNavOpen(false)}>
          {managerNav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-fg-muted hover:text-fg hover:bg-bg-elevated/40",
                  )
                }
                end={item.to === "/manager/dashboard"}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="rounded-lg bg-grad-primary p-3 text-primary-foreground">
            <p className="text-xs font-medium opacity-90">RM Area</p>
            <p className="text-[11px] opacity-75 mt-0.5">Signed in as {me.fullName ?? me.email}</p>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onMenuToggle={() => setNavOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
